import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  RdsAlertComponent,
  RdsBadgeComponent,
  RdsChipComponent,
  RdsEmptyStateComponent,
  RdsEmptyStateImageComponent,
  RdsEmptyStateSubtitleDirective,
  RdsEmptyStateTitleDirective,
  RdsIconComponent,
  RdsTabChangeEvent,
  RdsTabComponent,
  RdsTabLabelDirective,
  RdsTabsComponent,
  RdsTextButtonComponent,
} from '@rds/angular-components';
import { defaultIfEmpty, delay, switchMap } from 'rxjs';

import { ViewportService } from '@bookly/shared';
import { FavoriteService } from '@bookly-user/rooms/services/favorite.service';

import { RoomCardComponent } from '../../components/room-card/room-card.component';
import { FiltersFormService } from '../../components/room-filters-form/filters-form.service';
import { RoomsService } from '../../services/rooms.service';
import { CalendarOverviewComponent } from '../calendar-overview/calendar-overview.component';

@Component({
  selector: 'bus-rooms-list',
  imports: [
    NgTemplateOutlet,
    RoomCardComponent,
    RdsChipComponent,
    RdsTextButtonComponent,
    RdsTabsComponent,
    RdsTabComponent,
    CalendarOverviewComponent,
    RdsAlertComponent,
    RdsBadgeComponent,
    RdsTabLabelDirective,
    RdsEmptyStateComponent,
    RdsEmptyStateImageComponent,
    RdsEmptyStateTitleDirective,
    RdsEmptyStateSubtitleDirective,
    RdsIconComponent,
  ],
  templateUrl: './rooms-list.component.html',
  styleUrl: './rooms-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomsListComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly #roomsService = inject(RoomsService);
  readonly #roomFiltersService = inject(FiltersFormService);
  readonly #favoriteService = inject(FavoriteService);
  readonly #viewport = inject(ViewportService);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);
  readonly #document = inject(DOCUMENT);

  @ViewChild('scrollSentinel')
  private readonly scrollSentinel!: ElementRef<HTMLElement>;

  /** Min card width (px) from the grid CSS */
  private readonly cardMinWidth = 322;
  /** Gap between cards (px) from the grid CSS */
  private readonly cardGap = 48;
  /** Max container width (px) from the grid CSS */
  private readonly maxContainerWidth = 1650;
  private readonly desktopRowsPerPage = 8;
  private readonly mobileRowsPerPage = 12;
  private readonly preloadOffset = 280;

  private observer: IntersectionObserver | null = null;
  private readonly onResize = () => this.checkAndLoadMore();
  /** Prevents overlapping checkAndLoadMore executions. */
  private checkPending = false;

  protected readonly rooms = this.#roomsService.roomsList;
  protected readonly totalRoomsCount = this.#roomsService.totalRoomsCount;
  protected readonly hasMoreRooms = this.#roomsService.hasMoreRooms;
  protected readonly isLoading = computed(
    () =>
      this.#roomsService.isRoomListLoading() ||
      this.#roomsService.isRoomFiltersLoading() ||
      this.#favoriteService.isLoading()
  );
  // Show skeletons only for the first load; keep already rendered cards visible while loading next pages.
  protected readonly isInitialLoading = computed(
    () => this.isLoading() && (this.rooms()?.length ?? 0) === 0
  );
  protected readonly isLoadingMore = computed(
    () => this.isLoading() && (this.rooms()?.length ?? 0) > 0
  );
  protected readonly shouldShowEmptyState = computed(
    () =>
      this.rooms() !== null &&
      !this.isLoading() &&
      (this.rooms()?.length ?? 0) === 0
  );
  protected readonly skeletonCardCount = computed(() =>
    Math.max(6, Math.min(this.calculatePageSize(), 12))
  );
  protected readonly skeletonPlaceholders = computed(() =>
    Array.from({ length: this.skeletonCardCount() }, (_, index) => index)
  );
  protected readonly activeFiltersChips =
    this.#roomFiltersService.activeFiltersChips;
  protected readonly isFavoriteRoute =
    this.#route.snapshot.url[0].path === 'favorites';
  private readonly calendarTabIndex = 1;
  protected readonly isCalendarActivated = signal(false);

  constructor() {
    // Whenever any page-0 load completes (initial load, filter change from
    // desktop filter menu, etc.) check whether the sentinel is still visible
    // and keep loading until the screen is filled or all rooms are loaded.
    effect(() => {
      const rooms = this.#roomsService.roomsList();
      const page = this.#roomsService.currentPage();
      if (rooms !== null && page === 0) {
        this.checkAndLoadMore();
      }
    });
  }

  /** Returns the scrollable main container used by the dashboard layout. */
  private get scrollContainer(): HTMLElement | null {
    return this.#document.querySelector<HTMLElement>('.main-panel main');
  }

  /** Calculate how many tiles fit in 3 rows based on the current viewport width. */
  private calculatePageSize(): number {
    const containerWidth = Math.min(window.innerWidth, this.maxContainerWidth);
    const columns = Math.max(
      1,
      Math.floor(
        (containerWidth + this.cardGap) / (this.cardMinWidth + this.cardGap)
      )
    );
    const rowsPerPage = this.#viewport.isMobile()
      ? this.mobileRowsPerPage
      : this.desktopRowsPerPage;
    return columns * rowsPerPage;
  }

  /** Returns the active filters to use for a fresh (page-0) rooms request. */
  private getActiveFilters() {
    return this.isFavoriteRoute
      ? { favorite: true }
      : this.#roomFiltersService.getSelectedFormData();
  }

  /** Scroll the dashboard main panel back to the top. */
  private scrollToTop(): void {
    this.scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  ngOnInit() {
    this.#roomsService.clearRooms();
    this.#route.queryParamMap
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(params => {
        const isCalendar = params.get('calendarView') === 'true';
        this.isCalendarActivated.set(isCalendar);

        // Infinite scroll applies to the rooms grid only.
        if (!isCalendar) {
          this.checkAndLoadMore();
        }
      });
    if (this.isFavoriteRoute) {
      this.#roomsService
        .getRooms(this.getActiveFilters(), 0, this.calculatePageSize())
        .subscribe();
    } else {
      this.#roomsService
        .getRoomFilters()
        .pipe(
          defaultIfEmpty(null),
          delay(50),
          switchMap(() =>
            this.#roomsService.getRooms(
              this.getActiveFilters(),
              0,
              this.calculatePageSize()
            )
          )
        )
        .subscribe();
    }
  }

  ngAfterViewInit(): void {
    const container = this.scrollContainer;
    this.observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          this.checkAndLoadMore();
        }
      },
      {
        root: container,
        threshold: 0,
        rootMargin: `0px 0px ${this.preloadOffset}px 0px`,
      }
    );
    this.observer.observe(this.scrollSentinel.nativeElement);
    // Only window fires 'resize' events; containers do not.
    window.addEventListener('resize', this.onResize);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    window.removeEventListener('resize', this.onResize);
  }

  /** Loads the next page when the sentinel is visible and more rooms exist.
   * Deferred via setTimeout so Angular has rendered the new tiles before
   * measuring the sentinel's position. */
  private checkAndLoadMore(): void {
    // Prevent loading all pages while the calendar tab is active.
    if (this.isCalendarActivated()) {
      return;
    }

    if (this.checkPending) {
      return;
    }
    this.checkPending = true;
    setTimeout(() => {
      this.checkPending = false;
      if (!this.hasMoreRooms() || this.isLoading()) {
        return;
      }
      const sentinel = this.scrollSentinel?.nativeElement;
      if (!sentinel) {
        return;
      }
      // Use getBoundingClientRect() — gives viewport relative coordinates and
      // correctly accounts for scroll position inside the overflow container.
      const container = this.scrollContainer;
      const sentinelRect = sentinel.getBoundingClientRect();
      const isVisible = container
        ? sentinelRect.top <=
          container.getBoundingClientRect().bottom + this.preloadOffset
        : sentinelRect.top <= window.innerHeight + this.preloadOffset;
      if (isVisible) {
        this.#roomsService
          .loadMoreRooms()
          .subscribe({ complete: () => this.checkAndLoadMore() });
      }
    });
  }

  protected onTabChange(event: RdsTabChangeEvent): void {
    const isCalendar = event.index === this.calendarTabIndex;
    const isCalendarInUrl =
      this.#route.snapshot.queryParams['calendarView'] === 'true';
    if (isCalendar === isCalendarInUrl) {
      return;
    }
    void this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: { calendarView: isCalendar ? true : null },
      queryParamsHandling: 'merge',
      info: { skipViewTransition: true },
    });
  }

  protected onRoomUnfavorited(dexId: string): void {
    if (this.isFavoriteRoute) {
      this.#roomsService.removeRoom(dexId);
    }
  }

  protected closeChip(formControlName: string, formGroup: string | undefined) {
    this.#roomFiltersService.resetFormControl(formControlName, formGroup);
    if (this.#viewport.isMobile()) {
      this.scrollToTop();
      this.#roomsService
        .getRooms(this.getActiveFilters(), 0, this.calculatePageSize())
        .subscribe();
    }
  }

  protected resetFilers() {
    this.#roomFiltersService.resetForm();
    if (this.#viewport.isMobile()) {
      this.scrollToTop();
      this.#roomsService
        .getRooms(this.getActiveFilters(), 0, this.calculatePageSize())
        .subscribe();
    }
  }
}
